import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Dropdown, Grid, Input, Segment, Table } from "semantic-ui-react";
import { toast } from "react-toastify";
import { canonicalGroup, compareText, groupsOf, normaliseGroup } from "./appUserGroups";

import firebase from "../../../app/config/firebase";
const firestore = firebase.firestore();

const NO_GROUP = "__no_group__";
// Headings stay visible while a long list scrolls underneath them
const STICKY_HEADER = { position: "sticky", top: 0, zIndex: 1, background: "#f9fafb" };
const ALL_GROUPS = "__all__";
// Firestore allows 500 writes per batch
const BATCH_SIZE = 450;

// The App Users list with search, group filter and sortable columns, for groups of 200+ travellers
export default function AppUsersTable({ bookingId, client, clientUsers, onEdit, onDelete }) {
    const [search, setSearch] = useState("");
    const [groupFilter, setGroupFilter] = useState(ALL_GROUPS);
    const [sort, setSort] = useState({ column: "client_name", direction: "ascending" });
    // Ticks are kept while searching and filtering, so people from different searches can be set together
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkGroup, setBulkGroup] = useState("");
    const [saving, setSaving] = useState(false);

    // The main client's own login row is shown separately at the top, as before
    const travellers = useMemo(() => (clientUsers || []).filter((user) => user.client_email !== client?.client_email), [clientUsers, client]);
    const groups = useMemo(() => groupsOf(travellers), [travellers]);

    const groupOptions = useMemo(() => {
        const counts = {};
        travellers.forEach((user) => {
            const key = normaliseGroup(user.group).toLowerCase() || NO_GROUP;
            counts[key] = (counts[key] || 0) + 1;
        });
        return [
            { key: ALL_GROUPS, value: ALL_GROUPS, text: `All groups (${travellers.length})` },
            ...groups.map((group) => ({ key: group, value: group, text: `${group} (${counts[group.toLowerCase()] || 0})` })),
            ...(counts[NO_GROUP] ? [{ key: NO_GROUP, value: NO_GROUP, text: `No group (${counts[NO_GROUP]})` }] : []),
        ];
    }, [travellers, groups]);

    // When the filtered group empties (e.g. everyone in "No group" was just given a group), its option
    // disappears; fall back to all groups rather than showing a blank filter and an empty table
    useEffect(() => {
        if (groupFilter !== ALL_GROUPS && !groupOptions.some((option) => option.value === groupFilter)) {
            setGroupFilter(ALL_GROUPS);
        }
    }, [groupFilter, groupOptions]);

    const term = search.trim().toLowerCase();
    const matchesSearch = (name, email, group) =>
        !term ||
        [name, email, group].some((value) =>
            String(value || "")
                .toLowerCase()
                .includes(term),
        );

    const visible = useMemo(() => {
        const filtered = travellers.filter((user) => {
            const group = normaliseGroup(user.group);
            if (groupFilter === NO_GROUP && group) return false;
            if (groupFilter !== ALL_GROUPS && groupFilter !== NO_GROUP && group.toLowerCase() !== groupFilter.toLowerCase()) return false;
            return matchesSearch(user.client_name, user.client_email, group);
        });
        const factor = sort.direction === "ascending" ? 1 : -1;
        return filtered.sort((a, b) => {
            const primary = compareText(a[sort.column], b[sort.column]);
            // Blank groups sort last, then by name, so a group's members stay together
            if (sort.column === "group" && !a.group !== !b.group) return a.group ? -1 : 1;
            return primary * factor || compareText(a.client_name, b.client_name);
        });
    }, [travellers, groupFilter, term, sort]);

    // Ignore ticks for travellers who have since been deleted
    const selected = useMemo(() => {
        const ids = new Set(travellers.map((user) => user.id));
        return selectedIds.filter((id) => ids.has(id));
    }, [selectedIds, travellers]);
    const selectedSet = new Set(selected);
    const allVisibleSelected = visible.length > 0 && visible.every((user) => selectedSet.has(user.id));
    const someVisibleSelected = visible.some((user) => selectedSet.has(user.id));

    function toggleOne(id) {
        setSelectedIds((current) => (current.includes(id) ? current.filter((existing) => existing !== id) : [...current, id]));
    }

    // The heading checkbox ticks or unticks everyone currently showing
    function toggleVisible() {
        const visibleIds = visible.map((user) => user.id);
        setSelectedIds((current) => (allVisibleSelected ? current.filter((id) => !visibleIds.includes(id)) : [...new Set([...current, ...visibleIds])]));
    }

    async function applyGroup(value) {
        const group = canonicalGroup(value, groups);
        setSaving(true);
        try {
            for (let i = 0; i < selected.length; i += BATCH_SIZE) {
                const batch = firestore.batch();
                selected.slice(i, i + BATCH_SIZE).forEach((id) => {
                    batch.update(firestore.collection("bookings").doc(bookingId).collection("users").doc(id), { group });
                });
                await batch.commit();
            }
            const people = `${selected.length} traveller${selected.length === 1 ? "" : "s"}`;
            toast.success(group ? `${group} set for ${people}` : `Group removed for ${people}`);
            setSelectedIds([]);
            setBulkGroup("");
        } catch (error) {
            console.log(error);
            toast.error("Could not update the groups");
        } finally {
            setSaving(false);
        }
    }

    function sortBy(column) {
        setSort((current) => ({
            column,
            direction: current.column === column && current.direction === "ascending" ? "descending" : "ascending",
        }));
    }

    const showMainClient = client && client.appPassword && groupFilter === ALL_GROUPS && matchesSearch(client.client_name, client.client_email, "");

    if (travellers.length === 0 && !(client && client.appPassword)) {
        return "No Users";
    }

    const headerCell = (column, label) => (
        <Table.HeaderCell
            sorted={sort.column === column ? sort.direction : null}
            onClick={() => sortBy(column)}
            style={{ ...STICKY_HEADER, cursor: "pointer" }}
        >
            {label}
        </Table.HeaderCell>
    );

    return (
        <>
            <Grid>
                <Grid.Row columns={3} verticalAlign="middle">
                    <Grid.Column>
                        <Input icon="search" placeholder="Search name, email or group" value={search} onChange={(e, { value }) => setSearch(value)} fluid />
                    </Grid.Column>
                    <Grid.Column>
                        {groups.length > 0 && (
                            <Dropdown selection fluid options={groupOptions} value={groupFilter} onChange={(e, { value }) => setGroupFilter(value)} />
                        )}
                    </Grid.Column>
                    <Grid.Column textAlign="right" style={{ color: "#888" }}>
                        Showing {visible.length} of {travellers.length} travellers
                    </Grid.Column>
                </Grid.Row>
            </Grid>

            {selected.length > 0 && (
                <Segment secondary style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 10, marginBottom: 0 }}>
                    <strong>{selected.length} selected</strong>
                    <Input
                        size="small"
                        placeholder="Group, e.g. Bus 3"
                        list="bulk-groups"
                        autoComplete="off"
                        value={bulkGroup}
                        onChange={(e, { value }) => setBulkGroup(value)}
                    />
                    <datalist id="bulk-groups">
                        {groups.map((group) => (
                            <option key={group} value={group} />
                        ))}
                    </datalist>
                    <Button size="small" color="orange" disabled={!normaliseGroup(bulkGroup) || saving} loading={saving} onClick={() => applyGroup(bulkGroup)}>
                        Set group
                    </Button>
                    <Button size="small" disabled={saving} onClick={() => applyGroup("")}>
                        Remove group
                    </Button>
                    <Button size="small" basic disabled={saving} onClick={() => setSelectedIds([])}>
                        Clear selection
                    </Button>
                </Segment>
            )}

            <div style={{ maxHeight: "65vh", overflowY: "auto", marginTop: 10 }}>
                <Table celled sortable style={{ marginTop: 0 }}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell collapsing style={STICKY_HEADER}>
                                <Checkbox
                                    checked={allVisibleSelected}
                                    indeterminate={someVisibleSelected && !allVisibleSelected}
                                    disabled={visible.length === 0}
                                    onChange={toggleVisible}
                                    title="Select everyone showing"
                                />
                            </Table.HeaderCell>
                            {headerCell("client_name", "Name")}
                            {headerCell("client_email", "Email")}
                            <Table.HeaderCell style={STICKY_HEADER}>Password</Table.HeaderCell>
                            {headerCell("group", "Group")}
                            <Table.HeaderCell style={STICKY_HEADER}>App User</Table.HeaderCell>
                            <Table.HeaderCell style={STICKY_HEADER}>User Type</Table.HeaderCell>
                            <Table.HeaderCell style={STICKY_HEADER}>Actions</Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>

                    <Table.Body>
                        {showMainClient && (
                            <Table.Row key={client.id}>
                                <Table.Cell collapsing />
                                <Table.Cell>{client.client_name}</Table.Cell>
                                <Table.Cell>{client.client_email}</Table.Cell>
                                <Table.Cell>{client.appPassword}</Table.Cell>
                                <Table.Cell />
                                <Table.Cell>{client.appUserUid && client.appUserUid.length > 0 ? "Yes" : ""}</Table.Cell>
                                <Table.Cell>{client.userType}</Table.Cell>
                                <Table.Cell textAlign="right" width={3}></Table.Cell>
                            </Table.Row>
                        )}
                        {visible.map((user) => (
                            <Table.Row key={user.id} active={selectedSet.has(user.id)}>
                                <Table.Cell collapsing>
                                    <Checkbox checked={selectedSet.has(user.id)} onChange={() => toggleOne(user.id)} />
                                </Table.Cell>
                                <Table.Cell>{user.client_name}</Table.Cell>
                                <Table.Cell>{user.client_email}</Table.Cell>
                                <Table.Cell>{user.password}</Table.Cell>
                                <Table.Cell>{normaliseGroup(user.group)}</Table.Cell>
                                <Table.Cell>{user.appUserUid && user.appUserUid.length > 0 ? "Yes" : ""}</Table.Cell>
                                <Table.Cell>{user.userType}</Table.Cell>
                                <Table.Cell textAlign="right" width={3}>
                                    <Button color="orange" as="a" onClick={() => onEdit(user)}>
                                        Edit
                                    </Button>
                                    <Button size="medium" color="red" content="Delete" as="a" onClick={() => onDelete(user)} />
                                </Table.Cell>
                            </Table.Row>
                        ))}
                        {visible.length === 0 && !showMainClient && (
                            <Table.Row>
                                <Table.Cell colSpan={8} textAlign="center" style={{ color: "#888" }}>
                                    No travellers match
                                </Table.Cell>
                            </Table.Row>
                        )}
                    </Table.Body>
                </Table>
            </div>
        </>
    );
}
